import React from 'react';
import { SvgIconProps, Theme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import classNames from 'classnames';
import { ReactComponent as BrochureIcon } from './brochure.svg';
import { ReactComponent as CaseIcon } from './case.svg';
import { ReactComponent as CheckListIcon } from './check_list.svg';
import { ReactComponent as ContractIcon } from './contract.svg';
import { ReactComponent as CustomersIcon } from './customers.svg';
import { ReactComponent as DataRoomIcon } from './data_room.svg';
import { ReactComponent as DemoIcon } from './demo.svg';
import { ReactComponent as FeedbackLikeIcon } from './feedback_like.svg';
import { ReactComponent as FeedbackStarIcon } from './feedback_star.svg';
import { ReactComponent as IntergrationArrowsIcon } from './integration_arrows.svg';
import { ReactComponent as IntergrationBoxesIcon } from './intergration_boxes.svg';
import { ReactComponent as LegalIcon } from './legal.svg';
import { ReactComponent as MarketingIcon } from './marketing.svg';
import { ReactComponent as PresentationChartIcon } from './presentation_chart.svg';
import { ReactComponent as PresentationSales2Icon } from './presentation_chart_2.svg';
import { ReactComponent as PresentationSales3Icon } from './presentation_chart_3.svg';
import { ReactComponent as PriceIcon } from './price.svg';
import { ReactComponent as ProductIcon } from './product.svg';
import { ReactComponent as ProposalIcon } from './proposal.svg';
import { ReactComponent as OnboardingIcon } from './onboarding.svg';
import { ReactComponent as RecordIcon } from './record.svg';
import { ReactComponent as ReferenceIcon } from './reference.svg';
import { ReactComponent as SalesChartIcon } from './sales_chart.svg';
import { ReactComponent as SaleSlideIcon } from './sales_slide.svg';
import { ReactComponent as ScreenCaptureIcon } from './screen_capture.svg';
import { ReactComponent as SuccessIcon } from './success.svg';
import { ReactComponent as TargetIcon } from './target.svg';
import { ReactComponent as TestimonialLikeIcon } from './testimonial_like.svg';
import { ReactComponent as TestimonialStarIcon } from './testimonial_star.svg';
import { ReactComponent as VideoIcon } from './video.svg';
import { ReactComponent as VoiceIcon } from './voice.svg';
import { ReactComponent as WebinarIcon } from './webinar.svg';
import { ReactComponent as WhitepapersIcon } from './whitepapers.svg';
import { ReactComponent as FolderNotFound } from './folder_not_found.svg';

// Standalone illustration (not part of the content-type `iconList` picker) —
// shown above folder-view empty/error messages.
export { FolderNotFound };

export interface IconType {
  id: string;
  icon: typeof BrochureIcon;
  label: string;
  tags: string[];
}

interface IconComponentParams extends SvgIconProps {
  iconColor?: boolean;
  name: string;
}

export const iconList: IconType[] = [
  {
    id: 'brochure',
    label: 'Brochure',
    icon: BrochureIcon,
    tags: ['brochure', 'brochures'],
  },
  {
    id: 'case',
    label: 'Case',
    icon: CaseIcon,
    tags: ['case', 'case study', 'case studies', 'study', 'studies'],
  },
  {
    id: 'check_list',
    label: 'Checklist',
    icon: CheckListIcon,
    tags: [
      'checklist',
      'check',
      'list',
      'check-list',
      'check-lists',
      'lists',
      'task',
      'tasks',
      'tasklist',
      'tasklists',
      'todo',
      'to do',
      'case study',
      'case studies',
      'study',
      'studies',
      'case',
    ],
  },
  {
    id: 'contract',
    label: 'Proposals & Contracts',
    icon: ContractIcon,
    tags: [
      'proposals',
      'contracts',
      'proposal',
      'contract',
      'price',
      'pricing',
      'money',
    ],
  },
  {
    id: 'customers',
    label: 'Customers',
    icon: CustomersIcon,
    tags: ['customer', 'customers', 'onboard', 'onboarding'],
  },
  {
    id: 'data_room',
    label: 'Data Room',
    icon: DataRoomIcon,
    tags: ['data', 'room'],
  },
  {
    id: 'demo',
    label: 'Product Demonstrations',
    icon: DemoIcon,
    tags: [
      'product',
      'demo',
      'demos',
      'demonstration',
      'demonstrations',
      'record',
      'video',
      'play',
      'view',
      'watch',
    ],
  },
  {
    id: 'feedback_like',
    label: 'Feedback - Like',
    icon: FeedbackLikeIcon,
    tags: [
      'customer',
      'reference',
      'feedback',
      'like',
      'references',
      'customers',
    ],
  },
  {
    id: 'feedback_star',
    label: 'Feedback - Star',
    icon: FeedbackStarIcon,
    tags: [
      'customer',
      'reference',
      'feedback',
      'star',
      'references',
      'customers',
    ],
  },
  {
    id: 'intergration_arrows',
    label: 'Integration - Arrows',
    icon: IntergrationArrowsIcon,
    tags: [
      'integration',
      'integrations',
      'connection',
      'connections',
      'connect',
      'integrate',
      'arrow',
      'arrows',
    ],
  },
  {
    id: 'intergration_boxes',
    label: 'Integration - Boxes',
    icon: IntergrationBoxesIcon,
    tags: [
      'integration',
      'integrations',
      'connection',
      'connections',
      'connect',
      'integrate',
      'box',
      'boxes',
    ],
  },
  {
    id: 'legal',
    label: 'Legal',
    icon: LegalIcon,
    tags: ['legal', 'shield', 'protect', 'protection', 'security', 'secure'],
  },
  {
    id: 'marketing',
    label: 'Marketing Materials',
    icon: MarketingIcon,
    tags: [
      'marketing',
      'material',
      'materials',
      'market',
      'sample',
      'megaphone',
      'loundspeaker',
      'announce',
      'announcement',
    ],
  },
  {
    id: 'onboarding',
    label: 'Onboarding',
    icon: OnboardingIcon,
    tags: ['customer', 'customers', 'onboard', 'onboarding'],
  },
  {
    id: 'presentation_chart',
    label: 'Sales Presentations',
    icon: PresentationChartIcon,
    tags: [
      'sale',
      'sales',
      'presentation',
      'presentations',
      'preso',
      'deck',
      'slide',
      'slides',
      'chart',
      'charts',
      'graph',
      'graphs',
      'stat',
      'stats',
      'statistic',
      'statistics',
      'analytic',
      'analytics',
    ],
  },
  {
    id: 'presentation_chart_2',
    label: 'Sales Presentation 2',
    icon: PresentationSales2Icon,
    tags: [
      'sale',
      'sales',
      'presentation',
      'presentations',
      'preso',
      'deck',
      'slide',
      'slides',
      'stat',
      'stats',
      'statistic',
      'statistics',
      'analytic',
      'analytics',
      'percent',
      'percentage',
    ],
  },
  {
    id: 'presentation_chart_3',
    label: 'Sales Presentation 3',
    icon: PresentationSales3Icon,
    tags: [
      'sale',
      'sales',
      'presentation',
      'presentations',
      'preso',
      'deck',
      'slide',
      'slides',
      'stat',
      'stats',
      'statistic',
      'statistics',
      'analytic',
      'analytics',
      'play',
      'view',
      'watch',
    ],
  },
  {
    id: 'price',
    label: 'Price',
    icon: PriceIcon,
    tags: [
      'proposals',
      'contracts',
      'proposal',
      'contract',
      'price',
      'pricing',
      'money',
      'tag',
      'tags',
    ],
  },
  {
    id: 'product',
    label: 'Product',
    icon: ProductIcon,
    tags: [
      'product',
      'demo',
      'demos',
      'demonstration',
      'demonstrations',
      'star',
    ],
  },
  {
    id: 'proposal',
    label: 'Proposal',
    icon: ProposalIcon,
    tags: [
      'proposals',
      'contracts',
      'proposal',
      'contract',
      'price',
      'pricing',
      'money',
    ],
  },
  {
    id: 'record',
    label: 'Record',
    icon: RecordIcon,
    tags: [
      'customer',
      'reference',
      'feedback',
      'star',
      'references',
      'customers',
    ],
  },
  {
    id: 'reference',
    label: 'Reference',
    icon: ReferenceIcon,
    tags: [
      'product',
      'demo',
      'demos',
      'demonstration',
      'demonstrations',
      'record',
      'video',
      'play',
    ],
  },
  {
    id: 'sales_chart',
    label: 'Sales Chart',
    icon: SalesChartIcon,
    tags: [
      'sale',
      'sales',
      'presentation',
      'presentations',
      'preso',
      'deck',
      'slide',
      'slides',
      'chart',
      'charts',
      'graph',
      'graphs',
      'stat',
      'stats',
      'statistic',
      'statistics',
      'analytic',
      'analytics',
    ],
  },
  {
    id: 'sale_slide',
    label: 'Sales Slide',
    icon: SaleSlideIcon,
    tags: [
      'sale',
      'sales',
      'presentation',
      'presentations',
      'preso',
      'deck',
      'slide',
      'slides',
      'stat',
      'stats',
      'statistic',
      'statistics',
      'analytic',
      'analytics',
      'time',
      'money',
    ],
  },
  {
    id: 'screen_capture',
    label: 'Screen Capture',
    icon: ScreenCaptureIcon,
    tags: ['screen', 'capture'],
  },
  {
    id: 'study',
    label: 'Study',
    icon: LegalIcon,
    tags: [
      'study',
      'case study',
      'case studies',
      'inspect',
      'explore',
      'research',
      'discover',
      'case',
      'studies',
    ],
  },
  {
    id: 'success',
    label: 'Success',
    icon: SuccessIcon,
    tags: ['customer', 'reference', 'customers', 'references', 'success'],
  },
  {
    id: 'target',
    label: 'Target',
    icon: TargetIcon,
    tags: ['target', 'market', 'bullseye', 'arrow'],
  },
  {
    id: 'testimonial_like',
    label: 'Testimonial - Like',
    icon: TestimonialLikeIcon,
    tags: [
      'customer',
      'reference',
      'references',
      'customers',
      'testimonial',
      'testimonials',
      'like',
    ],
  },
  {
    id: 'testimonial_star',
    label: 'Testimonial - Star',
    icon: TestimonialStarIcon,
    tags: [
      'customer',
      'reference',
      'references',
      'customers',
      'testimonial',
      'testimonials',
      'star',
    ],
  },
  {
    id: 'video',
    label: 'Video',
    icon: VideoIcon,
    tags: ['video', 'videos', 'play', 'playback', 'view', 'watch'],
  },
  {
    id: 'voice',
    label: 'Customer References',
    icon: VoiceIcon,
    tags: [
      'customer',
      'reference',
      'customers',
      'references',
      'testimonial',
      'testimonials',
      'review',
      'reviews',
      'success',
      'story',
      'stories',
      'feedback',
      'voice',
      'interview',
      'interviews',
    ],
  },
  {
    id: 'Webinar',
    label: 'Webinar',
    icon: WebinarIcon,
    tags: ['webinar', 'webinars', 'attend', 'attendee', 'attendees'],
  },
  {
    id: 'whitepapers',
    label: 'Whitepapers',
    icon: WhitepapersIcon,
    tags: [
      'whitepaper',
      'whitepapers',
      'white paper',
      'white papers',
      'document',
      'documents',
    ],
  },
];

const useStyles = makeStyles((theme: Theme) => ({
  icon: {
    color: theme.palette.secondary.main,
  },
  // this class is used when the OrganizationThemeContextProvider loads after the className prop
  // to get the correct icon color from organization.colors.icon
  iconColor: {
    color: theme.palette.icon?.main,
  },
}));

const IconComponent: React.FC<IconComponentParams> = (
  props: IconComponentParams
): JSX.Element => {
  const classes = useStyles();
  const { iconColor, name, className, ...iconProps } = props;
  const IconElement = iconList.find((i) => i.id === name)?.icon;
  return IconElement ? (
    <IconElement
      {...iconProps}
      className={classNames(classes.icon, className, {
        [classes.iconColor]: iconColor,
      })}
    />
  ) : (
    <></>
  );
};

export default IconComponent;
